import React from 'react'
import { Alert, Box } from '@mui/material'
import { SESSION_INTERFACE_IDS } from 'src/live/domain'
import { readSessionRoutingDecision } from 'src/live/routing/sessionRoutingClient'
import { readLocalParticipantIdentity } from 'src/live/session'
import SharedRtcVideoSurface from 'src/live/shell/components/SharedRtcVideoSurface'

function normalizeOptionalString(value, fallbackValue = null) {
  if (typeof value !== 'string') {
    return fallbackValue
  }

  const normalizedValue = value.trim()

  return normalizedValue || fallbackValue
}

function buildViewerUsername(localParticipantIdentity = null) {
  return JSON.stringify({
    role: normalizeOptionalString(localParticipantIdentity?.role, 'auditor'),
    name: normalizeOptionalString(
      localParticipantIdentity?.displayName,
      'VC Audit Viewer',
    ),
    profilePicId: normalizeOptionalString(localParticipantIdentity?.profilePicId),
    identityKey: normalizeOptionalString(localParticipantIdentity?.participantId),
  })
}

function resolveAgoraPayload(viewerBootstrap, sessionRoutingDecision) {
  return (
    sessionRoutingDecision?.providerPayloads?.agora ||
    viewerBootstrap?.providerPayloads?.agora ||
    {}
  )
}

function buildAgoraRtcProps(viewerBootstrap, sessionRoutingDecision) {
  const agoraPayload = resolveAgoraPayload(viewerBootstrap, sessionRoutingDecision)
  const channel =
    normalizeOptionalString(agoraPayload?.channel) ||
    normalizeOptionalString(viewerBootstrap?.channel) ||
    normalizeOptionalString(viewerBootstrap?.bookedEvent?.id) ||
    normalizeOptionalString(viewerBootstrap?.session?.bookedEvent?.id)
  const uid =
    agoraPayload?.uid ??
    viewerBootstrap?.uid ??
    null
  const rtcToken =
    normalizeOptionalString(agoraPayload?.rtcToken) ||
    normalizeOptionalString(viewerBootstrap?.rtcToken)
  const appId =
    normalizeOptionalString(agoraPayload?.appId) ||
    normalizeOptionalString(import.meta.env.VITE_AGORA_APP_ID)

  if (!appId || !channel || uid === null || uid === undefined) {
    return null
  }

  return {
    appId,
    channel,
    uid,
    token: rtcToken || undefined,
    role: 'audience',
    enableAudio: false,
    enableVideo: false,
    enableScreensharing: false,
    callActive: true,
  }
}

function buildAgoraRtmProps(
  viewerBootstrap,
  sessionRoutingDecision,
  localParticipantIdentity,
  agoraRtcProps,
) {
  if (!agoraRtcProps?.appId || !agoraRtcProps?.channel || !agoraRtcProps?.uid) {
    return null
  }

  const agoraPayload = resolveAgoraPayload(viewerBootstrap, sessionRoutingDecision)
  const rtmToken =
    normalizeOptionalString(agoraPayload?.rtmToken) ||
    normalizeOptionalString(viewerBootstrap?.rtmToken)

  return {
    uid: String(agoraRtcProps.uid),
    username: buildViewerUsername(localParticipantIdentity),
    token: rtmToken || undefined,
    displayUsername: true,
    showPopUpBeforeRemoteMute: false,
    isThisUserAllowedToMuteOthers: false,
  }
}

export default function SupportAuditRtcVideoSurface({
  viewerBootstrap = null,
  handleLeaveRoom,
}) {
  const sessionRoutingDecision = React.useMemo(
    () => readSessionRoutingDecision(viewerBootstrap),
    [viewerBootstrap],
  )
  const localParticipantIdentity = React.useMemo(
    () => readLocalParticipantIdentity(viewerBootstrap, null),
    [viewerBootstrap],
  )
  const agoraRtcProps = React.useMemo(
    () => buildAgoraRtcProps(viewerBootstrap, sessionRoutingDecision),
    [sessionRoutingDecision, viewerBootstrap],
  )
  const agoraRtmProps = React.useMemo(
    () =>
      buildAgoraRtmProps(
        viewerBootstrap,
        sessionRoutingDecision,
        localParticipantIdentity,
        agoraRtcProps,
      ),
    [agoraRtcProps, localParticipantIdentity, sessionRoutingDecision, viewerBootstrap],
  )
  const providerFallback = (
    <Box
      sx={{
        height: '100%',
        minHeight: 360,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Alert severity="warning" sx={{ width: '100%', maxWidth: 640 }}>
        Live viewer is not available for the current session provider yet.
      </Alert>
    </Box>
  )

  return (
    <SharedRtcVideoSurface
      sessionRoutingDecision={sessionRoutingDecision}
      agoraRtcProps={agoraRtcProps}
      agoraRtmProps={agoraRtmProps}
      handleLeaveRoom={handleLeaveRoom}
      localParticipantIdentity={localParticipantIdentity}
      sessionInterfaceId={SESSION_INTERFACE_IDS.supportAuditObserver}
      providerFallback={providerFallback}
    />
  )
}

SupportAuditRtcVideoSurface.displayName = 'SupportAuditRtcVideoSurface'
